<script lang="ts">
import type { SubmitFunction } from "@sveltejs/kit";
import { applyAction, enhance } from "$app/forms";
import { type AccountConfirmation, deleteDialogMode } from "./delete-dialog";

// The account control of the global header: a profile button that opens a menu
// on click, and the confirmation that guards account deletion. Logging out
// submits a plain form to its own clean address, so it works without JavaScript
// as well.
//
// Deleting asks for proof that the person at the keyboard owns the account, and
// which proof it asks for is the API's decision, arriving as `confirmation`: a
// login with a password types it here, a login created through Google signs in
// again instead. The API checks the proof again when the form is submitted, so
// this dialog only shapes the request — it never decides whether it is allowed.
let {
  confirmation = null,
  reopenDelete = false,
}: { confirmation?: AccountConfirmation | null; reopenDelete?: boolean } = $props();

let menuOpen = $state(false);
let confirmOpen = $state(false);
let password = $state("");
let error = $state<string | null>(null);
let submitting = $state(false);

let menuRoot = $state<HTMLElement | null>(null);
let triggerButton = $state<HTMLButtonElement | null>(null);
let passwordInput = $state<HTMLInputElement | null>(null);
let reauthLink = $state<HTMLAnchorElement | null>(null);
let cancelButton = $state<HTMLButtonElement | null>(null);

const mode = $derived(deleteDialogMode(confirmation));
// A password has to be typed before the irreversible button arms, so the delete
// is never a single stray click. With no password to type, having just signed
// in again is what arms it.
const canDelete = $derived(mode === "password" ? password.length > 0 : mode === "reauthenticated");

function closeMenu(): void {
  menuOpen = false;
}

function openConfirm(): void {
  menuOpen = false;
  password = "";
  error = null;
  confirmOpen = true;
}

function closeConfirm(): void {
  confirmOpen = false;
  password = "";
  error = null;
  triggerButton?.focus();
}

// Coming back from signing in again lands on the cabinet's own address, with
// nothing about the errand in the URL; the server passes the flag instead, so
// the visitor picks up where they left off rather than on a bare dashboard.
$effect(() => {
  if (reopenDelete) confirmOpen = true;
});

// Move focus into the dialog when it opens, so a keyboard user lands on the
// control that carries the next step rather than back on the page behind it.
// Never on the destructive button: it is the one control nobody should reach by
// default.
$effect(() => {
  if (!confirmOpen) return;
  if (mode === "password") passwordInput?.focus();
  else if (mode === "reauthenticate") reauthLink?.focus();
  else cancelButton?.focus();
});

// A refusal keeps the dialog open with the reason next to the field, so the
// visitor can correct a typo without starting over; anything else (the
// redirect a completed deletion returns) is applied as usual.
const onDelete: SubmitFunction = () => {
  submitting = true;
  error = null;
  return async ({ result }) => {
    submitting = false;
    if (result.type === "failure") {
      const message = result.data?.message;
      error = typeof message === "string" ? message : "The account could not be deleted.";
      password = "";
      passwordInput?.focus();
      return;
    }
    await applyAction(result);
  };
};

function onWindowClick(event: MouseEvent): void {
  if (!menuOpen) return;
  if (menuRoot && !menuRoot.contains(event.target as Node)) closeMenu();
}

function onWindowKeydown(event: KeyboardEvent): void {
  if (event.key !== "Escape") return;
  if (confirmOpen) {
    closeConfirm();
  } else if (menuOpen) {
    closeMenu();
    triggerButton?.focus();
  }
}
</script>

<svelte:window onclick={onWindowClick} onkeydown={onWindowKeydown} />

<div class="relative" bind:this={menuRoot}>
  <button
    type="button"
    bind:this={triggerButton}
    onclick={() => (menuOpen = !menuOpen)}
    aria-haspopup="menu"
    aria-expanded={menuOpen}
    aria-controls="profile-menu"
    aria-label="Account menu"
    data-testid="profile-button"
    class="tap-target flex h-9 w-9 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-600 hover:bg-slate-100"
  >
    <svg
      class="h-5 w-5"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      stroke-width="1.8"
      stroke-linecap="round"
      stroke-linejoin="round"
      aria-hidden="true"
    >
      <circle cx="12" cy="8" r="3.5" />
      <path d="M4.5 19.5a7.5 7.5 0 0 1 15 0" />
    </svg>
  </button>

  {#if menuOpen}
    <div
      id="profile-menu"
      role="menu"
      data-testid="profile-menu"
      class="absolute right-0 z-50 mt-2 w-56 overflow-hidden rounded-md border border-slate-200 bg-white py-1 shadow-lg"
    >
      <!-- Errands about the account itself, kept out of the working sections in
           the bar. Top up is offered even on a deployment that takes no crypto:
           asking the API whether the deposit routes exist would cost every page
           a round trip to decide whether to draw a link, and the page it leads
           to answers the question anyway. -->
      {#if confirmation?.is_admin}
        <!-- Offered only to a login the API says reaches the console. It is a
             link and nothing more: following it lands on a page whose own guard
             decides, and for anybody else that page is a 404. -->
        <a
          href="/admin"
          role="menuitem"
          data-testid="menuitem-admin"
          onclick={closeMenu}
          class="tap-target-row block w-full px-4 py-2 text-left text-sm font-semibold text-slate-900 hover:bg-slate-100"
        >
          Admin console
        </a>
        <div class="my-1 border-t border-slate-200"></div>
      {/if}
      <a
        href="/app/keys"
        role="menuitem"
        data-testid="menuitem-keys"
        onclick={closeMenu}
        class="tap-target-row block w-full px-4 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
      >
        API keys
      </a>
      <a
        href="/app/top-ups"
        role="menuitem"
        data-testid="menuitem-top-ups"
        onclick={closeMenu}
        class="tap-target-row block w-full px-4 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
      >
        Top up
      </a>
      <a
        href="/app/settings"
        role="menuitem"
        data-testid="menuitem-settings"
        onclick={closeMenu}
        class="tap-target-row block w-full px-4 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
      >
        Settings
      </a>
      <div class="my-1 border-t border-slate-200"></div>
      <form method="POST" action="/logout">
        <button
          type="submit"
          role="menuitem"
          data-testid="menuitem-logout"
          class="tap-target-row block w-full px-4 py-2 text-left text-sm font-medium text-slate-700 hover:bg-slate-100"
        >
          Log out
        </button>
      </form>
      <button
        type="button"
        role="menuitem"
        data-testid="menuitem-delete"
        onclick={openConfirm}
        class="tap-target-row block w-full px-4 py-2 text-left text-sm font-medium text-red-600 hover:bg-red-50"
      >
        Delete account
      </button>
    </div>
  {/if}
</div>

{#if confirmOpen}
  <div
    class="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-6"
    role="dialog"
    aria-modal="true"
    aria-labelledby="delete-account-title"
    data-testid="delete-modal"
  >
    <div class="w-full max-w-md rounded-lg border border-slate-200 bg-white p-6 shadow-xl">
      <h2 id="delete-account-title" class="text-lg font-semibold text-slate-900">
        Delete account
      </h2>
      <p class="mt-2 text-sm text-slate-600">
        This cannot be undone. Your account, every API key, your credit balance and your
        operation history are erased permanently.
      </p>

      <form method="POST" action="/account/delete" use:enhance={onDelete}>
        {#if mode === "password"}
          <label class="mt-4 block text-sm font-medium text-slate-700" for="delete-password-input">
            Enter your account password to confirm
          </label>
          <input
            id="delete-password-input"
            name="password"
            bind:this={passwordInput}
            bind:value={password}
            type="password"
            autocomplete="current-password"
            data-testid="delete-password-input"
            class="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none"
          />
        {:else if mode === "reauthenticate"}
          <p class="mt-4 text-sm text-slate-700">
            This account signs in with Google, so there is no password to type. Confirm with
            Google and you will come back here to finish.
          </p>
          <a
            href="/account/delete/reauthenticate"
            bind:this={reauthLink}
            data-testid="delete-reauth-link"
            class="mt-3 inline-block rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Confirm with Google
          </a>
        {:else}
          <p class="mt-4 text-sm text-slate-700" data-testid="delete-reauth-confirmed">
            Your Google sign-in is confirmed. You can delete the account now.
          </p>
        {/if}

        {#if error}
          <p class="mt-3 text-sm text-red-600" role="alert" data-testid="delete-error">{error}</p>
        {/if}

        <div class="mt-6 flex justify-end gap-2">
          <button
            type="button"
            bind:this={cancelButton}
            onclick={closeConfirm}
            data-testid="delete-cancel-button"
            class="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          {#if mode !== "reauthenticate"}
            <button
              type="submit"
              disabled={!canDelete || submitting}
              data-testid="delete-confirm-button"
              class="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Delete permanently
            </button>
          {/if}
        </div>
      </form>
    </div>
  </div>
{/if}
